import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BarChartOutlined, ProfileOutlined } from "@ant-design/icons";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";


import { getItemPopupList } from "src/apis/itemApi";
import { InputForm } from "src/components/common/form/input/InputForm";
import { useDescText } from "src/hooks/useDescText";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig, InfoFieldHandlers } from "src/types/info-form";
import { DESC_FLD } from "src/utils/descLanguage/descLanguageUtil";
import { KSK2022_R01HeaderDto, KSK2022_R01LotDto } from "../Dto_KSK2022_R01";
import { useKSK2022_R01Store } from "../Store_KSK2022_R01";
import { InputForm_KSK2022_R01 as InputFormKSK2020R01 } from "./InputForm_KSK2022_R01";



interface Props {
	editable: boolean

	defaultKSK2022_R01HeaderRow: KSK2022_R01HeaderDto;

	handleFetchBomProductList: ( productOrderNo: string, itemCd: string, warehouseCd: string, logicCenter: string ) => void;
	handleFetchLotList: (onFail?: () => void) => void;

	getProductWorkFieldHandlers: (fieldName: string) => InfoFieldHandlers;
}

export const Info_KSK2022_R01 = (props: Props) => {

	const { editable, 
		defaultKSK2022_R01HeaderRow,
		handleFetchBomProductList, handleFetchLotList,
		getProductWorkFieldHandlers,
	} = props;

	const { itemStatusList, workPlaceList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { renderDescText, getFormTitleWidth } = useDescText();
	const { 
			editKSK2022_R01, setEditKSK2022_R01,
			isInitialLoad, setIsInitialLoad,
			KSK2022_R01LotList, setKSK2022_R01LotList,
	} = useKSK2022_R01Store();

	const [searchTrigger, setSearchTrigger] = useState(false);
	// 현재 언어에서 가장 긴 항목명을 기준으로 본 화면의 제목 너비를 맞춘다.
	const formTitleWidth = useMemo(() => getFormTitleWidth([
		renderDescText(DESC_FLD.WORK_ORDER, "작업지시"),
		renderDescText(DESC_FLD.WORK_INFO, "작업정보"),
		renderDescText(DESC_FLD.ITEM_CODE, "품목코드"),
		renderDescText(DESC_FLD.ITEM_NAME, "품명"),
		renderDescText(DESC_FLD.SPECIFICATION, "규격"),
		renderDescText(DESC_FLD.UNIT, "단위"),
		renderDescText(DESC_FLD.WORK_ORDER_REMAINING_QTY, "작업지시잔량"),
		renderDescText(DESC_FLD.GOOD_QTY, "생산양품량"),
		renderDescText(DESC_FLD.GRADE, "등급"),
	], 120, 180), [getFormTitleWidth, renderDescText]);
	const fieldWrapperOption = {
		wrapperStyle: "!w-full min-w-0 h-[38px] mb-[7px]",
		titleStyle: "w-[120px] min-w-[120px] h-[38px] flex items-center rounded-[8px] bg-[#e8eff8] px-[10px] text-left text-[14px] font-semibold leading-none whitespace-nowrap text-[#183153]",
		inputStyle: "flex-1 min-w-0 [&>input]:!h-[38px] [&>input]:!rounded-[8px] [&>input]:!border-[#cbd9eb] [&>input]:px-[10px]",
	};
	const dropdownWrapperOption = {
		wrapperStyle: "!w-full min-w-0 h-[38px] mb-[7px]",
		titleStyle: fieldWrapperOption.titleStyle,
		inputStyle: "flex-1 min-w-0 [&>select]:!h-[38px] [&>select]:!rounded-[8px] [&>select]:!border-[#cbd9eb] [&>select]:px-[8px]",
	};

	// Info 폼 코드 검색 필드 설정
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'itemCd',
            category: "품목",
            focusId: "itemCd_search",
			responseMapping: {
				itemCd : 'itemCd',
				itemNm : 'itemNm',
				itemSpec : 'itemSpec',
				itemUnit : 'itemUnit'
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getItemPopupList(code, '', '', '');
				return employeeResponse.data.data.find(value => value.itemCd === code);
			}
        },
	];

	// Info 폼 이벤트 훅 사용
	useInfoFormEvents({
		editData: editKSK2022_R01,
		setEditData: setEditKSK2022_R01,
		defaultData: defaultKSK2022_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'KSK2022_R01'
	});

	const containerRef = useRef<HTMLDivElement>(null);
	const infoRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// 초기 로드 시에는 Content에서 이미 호출했으므로 건너뜁니다.
		if (isInitialLoad) {
			setIsInitialLoad(false);
			return;
		}

		if(editKSK2022_R01?.productOrderNo && editKSK2022_R01?.itemCd && editKSK2022_R01?.outWarehouseCd && editKSK2022_R01?.logicCenter) {
			handleFetchBomProductList(editKSK2022_R01.productOrderNo, editKSK2022_R01.itemCd, editKSK2022_R01.outWarehouseCd, editKSK2022_R01.logicCenter);
		}
	}, [editKSK2022_R01?.outWarehouseCd, editKSK2022_R01?.logicCenter])

	useEffect(() => {
		if (searchTrigger) {
			handleFetchLotList();
			setSearchTrigger(false);
		}
	}, [searchTrigger]);

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		let closeTag = editKSK2022_R01?.closeTag || 'N';
		if(Number(editKSK2022_R01?.orderQtyView) === Number(editKSK2022_R01?.productQty)) {
			closeTag = 'Y';
		}else {
			closeTag = 'N';
		}

		//상태값이 바뀌면 lot리스트의 itemStatus의 값도 바뀌어야 함
		if(name === 'itemStatus') {
			const newLotList = KSK2022_R01LotList.map((item: KSK2022_R01LotDto) => ({
				...item,
				itemStatus: value as string
			}));
			setKSK2022_R01LotList(newLotList);
		}

		setEditKSK2022_R01({
			...defaultKSK2022_R01HeaderRow,
			...editKSK2022_R01,
			[name]: value,
			closeTag: closeTag
		});
	}, [editKSK2022_R01, defaultKSK2022_R01HeaderRow, KSK2022_R01LotList, setEditKSK2022_R01, setKSK2022_R01LotList]);

	return (
		<div className="w-full h-full min-w-0 overflow-hidden rounded-[12px] border border-[#d3e0f0] bg-white shadow-sm" ref={containerRef}>
			<div className="flex h-[36px] shrink-0 items-center gap-[10px] bg-gradient-to-r from-[#123f78] to-[#245d98] px-[16px] text-[17px] font-bold text-white">
				<ProfileOutlined />
				<span>{renderDescText(DESC_FLD.WORK_ORDER_SELECTION, "작업지시 선택")}</span>
			</div>
				<div
					className="hide-scrollbar h-[calc(100%_-_36px)] overflow-y-auto p-[14px]"
					ref={infoRef}
				>
					<InputFormKSK2020R01
						titleWidth={formTitleWidth}
						setSearchTrigger={setSearchTrigger}
						handleChange={handleChange} 
						handleFetchLotList={handleFetchLotList} 
						getProductWorkFieldHandlers={getProductWorkFieldHandlers}
					/>
					<DropDownForm
						title={renderDescText(DESC_FLD.WORK_INFO, "작업정보")}
						titleWidth={formTitleWidth}
						name="workplaceCd"
						initLabelLess={true}
						value={editKSK2022_R01?.workplaceCd}
						data={workPlaceList}
						editable={false}
						onChange={handleChange}
						wrapperOption={dropdownWrapperOption}
					/>
					<InputForm
						title={renderDescText(DESC_FLD.ITEM_CODE, "품목코드")}
						titleWidth={formTitleWidth}
						value={editKSK2022_R01?.itemCd || ""}
						editable={false}
						name="itemCd"
						onChange={handleChange}
						wrapperOption={fieldWrapperOption}
					/>
					<InputForm title={renderDescText(DESC_FLD.ITEM_NAME, "품명")} titleWidth={formTitleWidth} value={editKSK2022_R01?.itemNm || ""}
						editable={false}
						name="itemNm"
						onChange={handleChange}
						wrapperOption={fieldWrapperOption} />

					<InputForm title={renderDescText(DESC_FLD.SPECIFICATION, "규격")} titleWidth={formTitleWidth} value={editKSK2022_R01?.itemSpec || ""}
						editable={false}
						name="itemSpec"
						onChange={handleChange}
						wrapperOption={fieldWrapperOption} />
					<InputForm title={renderDescText(DESC_FLD.UNIT, "단위")} titleWidth={formTitleWidth} value={editKSK2022_R01?.itemUnit || ""}
						editable={false}
						name="itemUnit"
						onChange={handleChange}
						wrapperOption={fieldWrapperOption} />
					<InputForm title={renderDescText(DESC_FLD.WORK_ORDER_REMAINING_QTY, "작업지시잔량")} titleWidth={formTitleWidth} value={editKSK2022_R01?.orderQtyView || 0}
						editable={false}
						name="orderQtyView"
						onChange={handleChange}
						wrapperOption={fieldWrapperOption} />
					<div className="mb-[7px] flex h-[38px] w-full items-center rounded-[8px] bg-[#d5f2f5] px-[12px] text-[#0b7085]">
						<BarChartOutlined className="mr-[8px] text-[15px]" />
						<span className="text-[14px] font-semibold">
							{renderDescText(DESC_FLD.CUMULATIVE_PRODUCTION_QTY, "누적 생산량")}
						</span>
					</div>
					{/* <InputForm title="작업완료량" value={editKSK2022_R01?.resultQty || 0}
						editable={false}
						name="resultQty"
						onChange={handleChange} />
					<BRDiv /> */}
					<InputForm title={renderDescText(DESC_FLD.GOOD_QTY, "생산양품량")} titleWidth={formTitleWidth} value={editKSK2022_R01?.productQty || 0}
						editable={false}
						name="productQty"
						onChange={handleChange}
						wrapperOption={fieldWrapperOption} />
					<DropDownForm
						title={renderDescText(DESC_FLD.GRADE, "등급")}
						titleWidth={formTitleWidth}
						name="itemStatus"
						initLabelLess={true}
						value={editKSK2022_R01?.itemStatus}
						data={itemStatusList}
						editable={false}
						onChange={handleChange}
						wrapperOption={{ ...dropdownWrapperOption, wrapperStyle: "!w-full min-w-0 h-[38px]" }}
					/>

				</div>
		</div>
	)
}
